import { useQuery } from '@tanstack/react-query';
import { useEffect, useRef } from 'react';
import { identifyFounder, trackConversion } from '../lib/analytics.js';
import { claimSignupConversionApi } from '../lib/api.js';
import { meQuery } from '../lib/queries.js';

// Mounted once at app root (main.tsx) inside the QueryClientProvider.
// Renders nothing; its whole job is to tell the analytics tag who the
// current founder is, once per signed-in identity — and to report the
// `account_created` conversion for a brand-new account.
//
// This lives here rather than next to each login call for one reason: there
// are four ways into a session (password, magic link, Google, and simply
// returning with a live cookie), and three of them never run a success
// handler on this origin — the OAuth and magic-link legs arrive as a fresh
// document load. Gating on meQuery catches all four in one place, which is
// also what NotificationStreamMount does for the SSE stream.
//
// The ref makes it once-per-id rather than once-per-render: meQuery refetches
// on focus, and re-identifying the same person on every window focus would
// bill an event for nothing.

export function AnalyticsIdentityMount(): null {
  const { data: me } = useQuery(meQuery());
  const identifiedRef = useRef<string | null>(null);
  const conversionRef = useRef<string | null>(null);

  useEffect(() => {
    if (!me) return;
    if (identifiedRef.current === me.id) return;
    identifiedRef.current = me.id;
    // Traits describe the account, not the person — role/locale/org are what
    // make "who converts" answerable. Email and name are deliberately absent:
    // nothing here needs them, and the tag is the wrong place to put them.
    identifyFounder(me.id, {
      workspace_role: me.workspaceRole,
      organization_id: me.organizationId,
      locale: me.locale,
      designer_only: Boolean(me.designerOnly),
    });
  }, [me]);

  // The `account_created` conversion for every signup path, password and
  // OAuth alike. It fires HERE rather than in signup.tsx because the Google
  // leg never returns to that component: the browser leaves for Google and
  // comes back as a fresh document load, so a success handler on the signup
  // route can only ever catch the password case. That asymmetry was the bug
  // (task 5f7a2f1) — Google signups reported nothing, and a funnel reading
  // zero looked identical whether nobody signed up or we simply failed to
  // record it.
  //
  // Firing from the dashboard keeps attribution: both tags carry
  // data-cookie-domain=".magig.app", so the gclid captured on the landing is
  // readable here, and conversions are exempt from this tag's
  // data-exclude-paths="/*".
  //
  // Whether the account is NEW is the server's call, not a guess from this
  // route — see POST /api/me/signup-conversion/claim. The claim is
  // exactly-once server-side, so the ref below is an optimisation against
  // meQuery's focus refetches, not the thing preventing a double count.
  useEffect(() => {
    if (!me) return;
    if (conversionRef.current === me.id) return;
    conversionRef.current = me.id;
    void claimSignupConversionApi().then((claim) => {
      if (!claim.claimed) return;
      trackConversion('account_created', {
        method: claim.method ?? 'unknown',
        // An invited teammate joining an existing workspace is a different
        // conversion from a founder arriving cold, and the ad spend only
        // chases the second one.
        invited: Boolean(claim.invited),
      });
    });
  }, [me]);

  return null;
}
